import { Box, css, IconButton, styled, Typography } from "@mui/material";
import { ChevronLeft, Clear } from "@procore/core-icons";
import { colors, GhostPlaceholder } from "@procore/core-react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useModelRevisions } from "apis/procore/modelRevisions";
import { useGetModelsQuery } from "apis/procore/procoreApi";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { selectIsLargeMapMobileOpen, setLargeMapMobileOpen } from "features/miniMap/minimapSlice";
import {
    isDevForceMobile,
    selectIsProcoreAndroid,
    selectProcoreContext,
    selectProjectName,
} from "slices/explorer/selectors";

import { PROCORE_TOP_BAR, TOOLBAR_BUTTON, TOOLBAR_COLORS, TOOLBAR_CONTAINER } from "../constants";

const ControlsContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isPhonePortrait",
})<{ isPhonePortrait?: boolean }>`
    display: flex;
    align-items: ${({ isPhonePortrait }) => (isPhonePortrait ? "flex-start" : "center")};
    gap: 8px;
    /* In portrait, fill available width */
    ${({ isPhonePortrait }) => isPhonePortrait && "width: 100%;"}
`;

const ControlBox = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${TOOLBAR_CONTAINER.PADDING}px;
    background-color: ${PROCORE_TOP_BAR.CONTROL_BG};
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-radius: ${TOOLBAR_CONTAINER.RADIUS}px;
    border: ${TOOLBAR_CONTAINER.BORDER};
    box-shadow: ${TOOLBAR_CONTAINER.SHADOW};
`;

/** Close button box - 40x40 */
const CloseBox = styled(ControlBox)`
    position: relative;
    width: ${PROCORE_TOP_BAR.CLOSE_SIZE}px;
    height: ${PROCORE_TOP_BAR.CLOSE_SIZE}px;
    padding: 0;
    flex-shrink: 0;
    background-color: ${PROCORE_TOP_BAR.CLOSE_BG};
`;

/** Merged dropdown box containing model name - horizontal layout */
const MergedDropdownBox = styled(ControlBox)`
    height: ${PROCORE_TOP_BAR.CLOSE_SIZE}px;
    overflow: hidden;

    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: none;
    background-color: ${PROCORE_TOP_BAR.NAME_BG};
    color: ${TOOLBAR_COLORS.DEFAULT};
    font-size: 14px;

    svg {
        width: 18px;
        height: 18px;
        color: ${TOOLBAR_COLORS.DISABLED};
        flex-shrink: 0;
    }
`;

/** Vertical dropdown box for phone portrait - stacked name/revision, fills available space */
const VerticalDropdownBox = styled(ControlBox)`
    padding: 8px 16px;
    gap: 2px;
    flex: 1;
    min-width: 0; /* Allow flex shrinking */
    background-color: ${PROCORE_TOP_BAR.NAME_BG};
`;

const closeControlStyles = `
    width: 100%;
    height: 100%;
    padding: 0;
    border-radius: ${TOOLBAR_BUTTON.RADIUS}px;
    background-color: transparent;
    color: ${TOOLBAR_COLORS.DEFAULT};
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    touch-action: manipulation;

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background-color: ${TOOLBAR_COLORS.HOVER};
        }
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

const CloseButton = styled(IconButton)`
    ${closeControlStyles}
`;

const CloseLink = styled("a")`
    ${closeControlStyles}
`;

/** Vertical dropdown button for phone portrait */
const VerticalDropdownButton = styled("div")(
    () => css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        padding: 0;
        border: none;
        background-color: transparent;
        color: ${TOOLBAR_COLORS.DEFAULT};
        text-align: left;
        width: 100%;
        min-width: 0; /* Allow flex shrinking */
    `,
);

const NameRow = styled(Box)`
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    min-width: 0; /* Allow flex shrinking */
`;

interface SceneControlsProps {
    isPhonePortrait?: boolean;
}

/**
 * Scene controls - left side of top bar
 *
 * Normal mode: Close button + horizontal pill with model name
 * Phone portrait: Close button + vertical box with model name
 */
const PROCORE_ANDROID_CLOSE_URL = "procoremobile://app.procore.com/embedded_webview/close?reason=user_closed";

function StackedCloseControls({
    isProcoreAndroid,
    isLargeMapMobileOpen,
    onBackFromLargeMap,
    onClose,
}: {
    isProcoreAndroid: boolean;
    isLargeMapMobileOpen: boolean;
    onBackFromLargeMap: () => void;
    onClose: () => void;
}) {
    const exitLayerSx = {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: isLargeMapMobileOpen ? 0 : 1,
        visibility: (isLargeMapMobileOpen ? "hidden" : "visible") as "hidden" | "visible",
        pointerEvents: (isLargeMapMobileOpen ? "none" : "auto") as "none" | "auto",
    };

    return (
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            <Box sx={exitLayerSx} aria-hidden={isLargeMapMobileOpen}>
                {isProcoreAndroid ? (
                    <CloseLink href={PROCORE_ANDROID_CLOSE_URL} title="Close" data-action="exit">
                        <Clear />
                    </CloseLink>
                ) : (
                    <CloseButton onClick={onClose} title="Close" data-action="exit" disableRipple disableFocusRipple>
                        <Clear />
                    </CloseButton>
                )}
            </Box>
            {isLargeMapMobileOpen ? (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CloseButton
                        onClick={onBackFromLargeMap}
                        title="Back"
                        data-action="back"
                        disableRipple
                        disableFocusRipple
                        sx={{ width: "100%", height: "100%" }}
                    >
                        <ChevronLeft />
                    </CloseButton>
                </Box>
            ) : null}
        </Box>
    );
}

export function SceneControls({ isPhonePortrait }: SceneControlsProps) {
    const dispatch = useAppDispatch();
    const isProcoreAndroid = useAppSelector(selectIsProcoreAndroid);
    const isLargeMapMobileOpen = useAppSelector(selectIsLargeMapMobileOpen);
    const procoreContext = useAppSelector(selectProcoreContext);

    const { data: modelRevisionsData, isLoading: isModelRevisionsLoading } = useModelRevisions();
    const modelRevision = modelRevisionsData?.[0];

    const { data: modelsData, isLoading: isModelsLoading } = useGetModelsQuery(
        procoreContext
            ? {
                  companyId: procoreContext.companyId,
                  projectId: procoreContext.projectId,
              }
            : skipToken,
    );
    const model = modelRevision ? modelsData?.data.find((m) => m.id === modelRevision.bim_model_id) : undefined;
    const projectName = useAppSelector(selectProjectName);
    // Production behavior is unchanged: only the real Procore model name is shown.
    // In `npm run dev` with `?forceMobile` (no Procore backend), fall back to the loaded
    // project name, then to a placeholder, so the header styling can be inspected.
    const modelLabel = model?.name || (isDevForceMobile ? projectName || "Sample model name" : "");

    const isLoading = isModelRevisionsLoading || isModelsLoading;

    const handleClose = () => {
        console.log("Close clicked");
    };

    const handleBackFromLargeMap = () => {
        dispatch(setLargeMapMobileOpen(false));
    };

    // Phone portrait: vertical layout
    if (isPhonePortrait) {
        return (
            <ControlsContainer isPhonePortrait>
                <CloseBox>
                    <StackedCloseControls
                        isProcoreAndroid={isProcoreAndroid}
                        isLargeMapMobileOpen={isLargeMapMobileOpen}
                        onBackFromLargeMap={handleBackFromLargeMap}
                        onClose={handleClose}
                    />
                </CloseBox>

                {isLoading ? (
                    <VerticalDropdownBox>
                        <VerticalDropdownButton>
                            <Box sx={{ width: "140px" }}>
                                <GhostPlaceholder startGradientColor={colors.gray40} endGradientColor={colors.gray94} />
                            </Box>
                        </VerticalDropdownButton>
                    </VerticalDropdownBox>
                ) : modelLabel ? (
                    <VerticalDropdownBox>
                        <VerticalDropdownButton>
                            <NameRow>
                                <Typography
                                    variant="body1"
                                    fontWeight={600}
                                    noWrap
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        fontSize: 16,
                                        lineHeight: "20px",
                                        color: TOOLBAR_COLORS.DEFAULT,
                                    }}
                                >
                                    {modelLabel}
                                </Typography>
                                {/* <KeyboardArrowDown
                                    sx={{ width: 18, height: 18, color: TOOLBAR_COLORS.DISABLED, flexShrink: 0 }}
                                /> */}
                            </NameRow>
                        </VerticalDropdownButton>
                    </VerticalDropdownBox>
                ) : null}
            </ControlsContainer>
        );
    }

    return (
        <ControlsContainer>
            <CloseBox>
                <StackedCloseControls
                    isProcoreAndroid={isProcoreAndroid}
                    isLargeMapMobileOpen={isLargeMapMobileOpen}
                    onBackFromLargeMap={handleBackFromLargeMap}
                    onClose={handleClose}
                />
            </CloseBox>

            {isLoading ? (
                <MergedDropdownBox>
                    <Box sx={{ width: "140px" }}>
                        <GhostPlaceholder startGradientColor={colors.gray40} endGradientColor={colors.gray94} />
                    </Box>
                </MergedDropdownBox>
            ) : modelLabel ? (
                <MergedDropdownBox>
                    <Typography
                        variant="body1"
                        fontWeight={600}
                        noWrap
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: 16,
                            lineHeight: "20px",
                            color: TOOLBAR_COLORS.DEFAULT,
                        }}
                    >
                        {modelLabel}
                    </Typography>
                </MergedDropdownBox>
            ) : null}
        </ControlsContainer>
    );
}
